import React from "react"
import PropTypes from "prop-types"
import { sanitizer } from "@commonUtils"
import Price from "@components/Price/Price"
import RatingBadge from "@components/RatingBadge/RatingBadge"
import Tag from "@components/Tag/Tag"
import TextualButton from "@components/Buttons/TextualButton/TextualButton"
import ImageLoader from "@components/ImageLoader/ImageLoader"
import css from "./SKUHorizontalTile.scss"

function SKUHorizontalTile(props) {
    const {
        image,
        line1,
        isChecked,
        iconAddHandler,
        iconCheckedHandler,
        tags,
        line2,
        line3,
        line4,
        rating,
        price,
        ctaLabel,
        ctaHandler,
    } = props

    const iconAction =
        iconAddHandler && iconCheckedHandler && (isChecked ? iconCheckedHandler : iconAddHandler)

    // An exception where we align items to the center if we have any permutation of [line1(mandatory), image, iconHandlers]
    const centerAlignedVariant = !(
        tags ||
        line2 ||
        line3 ||
        line4 ||
        rating ||
        price ||
        (ctaLabel && ctaHandler)
    )

    const handleClick = (e, handler) => {
        e.stopPropagation()
        handler()
    }

    const renderTags = (tags) => tags.map((tag, index) => <Tag key={index} {...tag} />)

    return (
        <div className={`flex pointer ${centerAlignedVariant ? "alignCenter" : css.skuContainer}`}>
            {image?.url && image?.alt && (
                <div className={`flex justifyCenter alignBase ${css.skuImage}`}>
                    <ImageLoader src={image.url} title={image?.alt} alt={image?.alt} height="36" width="36" />
                </div>
            )}
            <div className={`flexColumn ${css.skuBody}`}>
                {tags?.length ? <div className="flex flexWrap">{renderTags(tags)}</div> : null}
                {line1 && (
                    <div
                        className={`textPrimary smallRegular ${tags && tags.length ? "marginTop-4" : ""}`}
                        dangerouslySetInnerHTML={{
                            __html: sanitizer(line1),
                        }}
                    />
                )}
                {line2 && (
                    <div
                        className="textTertiary xSmallRegular"
                        dangerouslySetInnerHTML={{
                            __html: sanitizer(line2),
                        }}
                    />
                )}
                {line3 && (
                    <div
                        className="textSecondary xSmallRegular"
                        dangerouslySetInnerHTML={{
                            __html: sanitizer(line3),
                        }}
                    />
                )}
                {line4 && (
                    <div
                        className="textSecondary xSmallRegular"
                        dangerouslySetInnerHTML={{
                            __html: sanitizer(line4),
                        }}
                    />
                )}
                {rating?.averageRating && (
                    <div className="marginTop-4">
                        <RatingBadge
                            averageRating={rating.averageRating}
                            totalRatings={rating.totalRatings}
                            showRatingCount
                            showText
                        />
                    </div>
                )}
                {price?.mrp && (
                    <div className="flex marginTop-4">
                        <Price
                            price={price.mrp}
                            discountedPrice={price.discountedPrice}
                            discountPercent={price.discount}
                            variantSmall
                        />
                    </div>
                )}
                {ctaLabel && ctaHandler && (
                    <div className="marginTop-8">
                        <TextualButton label={ctaLabel} onClick={(e) => handleClick(e, ctaHandler)} />
                    </div>
                )}
            </div>
            {iconAction && (
                <div className={`flex ${css.skuIcon}`} onClick={(e) => handleClick(e, iconAction)}>
                    {isChecked ? <CheckedIcon /> : <AddIcon />}
                </div>
            )}
        </div>
    )
}

function AddIcon() {
    return (
        <svg width="24" height="24" fill="none" xmlns="http://www.w3.org/2000/svg" data-testid="addIcon">
            <rect width="24" height="24" rx="4" fill="#F0F0F0" />
            <path d="M3 21h18V3H3v18Z" fill="#3B3B3B" fillOpacity=".01" />
            <path
                d="M17.61 11.55h-5.05V6.5a.5.5 0 1 0-1 0v5.05H6.5a.5.5 0 1 0 0 1h5.05v5.06a.5.5 0 0 0 1 0v-5.05h5.06a.5.5 0 0 0 0-1Z"
                fill="#3B3B3B"
            />
        </svg>
    )
}

function CheckedIcon() {
    return (
        <svg width="24" height="24" fill="none" xmlns="http://www.w3.org/2000/svg" data-testid="checkedIcon">
            <rect width="24" height="24" rx="4" fill="#E7F7E5" />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M9.35 16.43c-.39 0-.77-.15-1.07-.44l-2.16-2.13a.5.5 0 0 1 0-.7c.19-.2.5-.2.7-.01L9 15.28c.2.2.53.2.73 0l7.45-7.56a.5.5 0 0 1 .72.7l-7.46 7.56c-.3.3-.69.45-1.08.45Z"
                fill="#208376"
            />
        </svg>
    )
}

SKUHorizontalTile.propTypes = {
    isChecked: PropTypes.bool,
    image: PropTypes.shape({ url: PropTypes.string.isRequired, alt: PropTypes.string.isRequired }).isRequired,
    line1: PropTypes.string.isRequired,
    line2: PropTypes.string,
    line3: PropTypes.string,
    line4: PropTypes.string,
    tags: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            color: PropTypes.string,
            backgroundColor: PropTypes.string,
            borderColor: PropTypes.string,
        })
    ),
    rating: PropTypes.shape({
        averageRating: PropTypes.number.isRequired,
        totalRatings: PropTypes.string,
        showRatingCount: PropTypes.bool,
        showText: PropTypes.bool,
    }),
    price: PropTypes.shape({
        mrp: PropTypes.number.isRequired,
        discount: PropTypes.string,
        discountedPrice: PropTypes.number,
        variantSmall: PropTypes.bool,
    }),
    ctaLabel: PropTypes.string,
    ctaHandler: PropTypes.func,
    iconAddHandler: PropTypes.func,
    iconCheckedHandler: PropTypes.func,
}

export default SKUHorizontalTile
